import React, { Component } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Divider,
    Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { CartContext } from "../../comp/CartContext";
import { WithRouter } from '../../comp/WithRouter';

// Lista base dos passos do Timeline
const STEPS_CONFIG = [
    { key: 'received', label: 'Aguardando aprovação', icon: <CheckCircleOutlineIcon /> },
    { key: 'em_producao', label: 'Pedido em preparação', icon: <Inventory2OutlinedIcon /> },
    { key: 'pronto', label: 'Pedido pronto', icon: <StorefrontOutlinedIcon /> },
    { key: 'em_rota', label: 'Em Rota', icon: <LocalShippingIcon /> },
    { key: 'entregues', label: 'Pedido concluído', icon: <DoneAllOutlinedIcon /> },
];

class DetalhesPedido extends Component {
    static contextType = CartContext;

    constructor(props) {
        super(props);
        this.state = {
            data: null,
            loader: true,
        };

        // Guarda a referência da última atualização para impedir re-renderizações infinitas
        this.lastProcessedUpdate = null;
    }

    componentDidMount() {
        this.carregarPedido();
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }

    componentWillUnmount() {
        // Limpa o evento ao sair da página
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }

    carregarPedido = () => {
        fetch("/api/store/order/" + this.props.params.id, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => {
                if (response.status === 200) {
                    response.json().then((data) => {
                        this.setState({ data, loader: false });
                    });
                } else {
                    this.setState({ loader: false });
                }
            })
            .catch(() => this.setState({ loader: false }));
    };

    componentDidUpdate(prevProps, prevState) {
        // 1. Obtém o evento SSE transmitido via CartContext
        const lastOrderUpdate = this.context?.lastOrderUpdate;

        // 2. ID do pedido desta tela
        const currentOrderId = this.props.params?.id || this.state.data?.id;

        // 3. Verifica se o SSE pertence a este pedido e evita re-renderização em loop
        if (
            lastOrderUpdate &&
            currentOrderId &&
            String(lastOrderUpdate.id) === String(currentOrderId) &&
            lastOrderUpdate !== this.lastProcessedUpdate
        ) {
            // Salva a referência para ignorar chamadas duplicadas
            this.lastProcessedUpdate = lastOrderUpdate;

            console.log("⚡ Novo status recebido via SSE:", lastOrderUpdate.status);

            const novoStatus = lastOrderUpdate.status;
            const dataAtual = new Date().toISOString();

            this.setState((prevState) => {
                const historicoAtual = Array.isArray(prevState.data?.historico_status)
                    ? [...prevState.data.historico_status]
                    : [];

                // Verifica se o novo status já existe no histórico
                const jaExiste = historicoAtual.some(item => item.status === novoStatus);

                // Adiciona o novo status no histórico com a hora atual se ainda não estiver lá
                if (!jaExiste) {
                    historicoAtual.push({
                        status: novoStatus,
                        created_at: dataAtual
                    });
                }

                return {
                    data: {
                        ...prevState.data,
                        status: novoStatus,              // Atualiza 'status'
                        delivery_status: novoStatus,     // Atualiza 'delivery_status' (usado pelo STEPS_CONFIG no seu render)
                        historico_status: historicoAtual  // Atualiza 'historico_status' (para acender a timeline)
                    },
                };
            });
        }
    }

    handleVisibilityChange = () => {
        // Se a tela foi desbloqueada / aba ficou visível novamente
        if (document.visibilityState === "visible") {
            console.log("📱 Tela desbloqueada! Recarregando dados do pedido...");
            this.carregarPedido();
        }
    };

    formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };
    render() {
        const { data, loader } = this.state;

        if (loader) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1">Carregando detalhes do pedido...</Typography>
                </Box>
            );
        }

        if (!data) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1">Pedido não encontrado.</Typography>
                </Box>
            );
        }

        const isConcluido = data.delivery_status === 'entregues';

        const historicoMap = {};
        if (Array.isArray(data.historico_status)) {
            data.historico_status.forEach((item) => {
                historicoMap[item.status] = item.created_at;
            });
        }

        const subtotalCalculado = (data.itens || []).reduce(
            (acc, item) => acc + Number(item.valor_total || item.valor_venda * item.quantidade || 0),
            0
        );
        const valorExibicao = data.total_venda > 0 ? data.total_venda : subtotalCalculado;

        const currentStepConfig = STEPS_CONFIG.find(step => step.key === data.delivery_status);
        const currentStatusText = currentStepConfig ? currentStepConfig.label : data.delivery_status;

        return (
            <Box
                sx={{
                    margin: '0 auto',
                    bgcolor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    fontFamily: 'Roboto, sans-serif',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                {/* Cabeçalho */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: '1px solid #E0E0E0',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1F2D3D', fontSize: '1.1rem' }}>
                        Detalhes do pedido
                    </Typography>
                    <IconButton size="small" onClick={() => this.props.navigate('/pedidos')} sx={{ bgcolor: '#ECEFF1' }}>
                        <CloseIcon fontSize="small" sx={{ color: '#546E7A' }} />
                    </IconButton>
                </Box>

                {/* Banner Destacado quando o Pedido é Concluído */}
                {isConcluido && (
                    <Box
                        sx={{
                            bgcolor: '#E8F5E9',
                            borderBottom: '1px solid #C8E6C9',
                            p: 2,
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 40 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
                            Pedido Concluído!
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#388E3C' }}>
                            Seu pedido foi entregue com sucesso.
                        </Typography>
                    </Box>
                )}

                {/* Conteúdo Principal */}
                <Box sx={{ p: 2 }}>
                    {/* Previsão / Origem */}
                    {!isConcluido && (
                        <Box sx={{ textAlign: 'center', my: 1.5 }}>
                            <Typography variant="caption" sx={{ color: '#78909C', fontWeight: '500', textTransform: 'uppercase' }}>
                                {data.origem === 'pickup' ? 'Previsão para retirada' : 'Previsão para entrega'}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2D3D', mt: 0.5 }}>
                                {this.formatDate(data.data_venda).split(',')[1] || '--:--'}
                            </Typography>
                        </Box>
                    )}

                    {/* Status Atual */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: isConcluido ? 0 : 2,
                            mb: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: data.status === 'canceled' ? '#D32F2F' : '#7CB342',
                                }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#263238' }}>
                                {data.status === 'canceled' ? 'Pedido Cancelado' : currentStatusText}
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: '#D32F2F' }}>
                            <ExpandLessIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{ borderStyle: 'dashed', my: 1.5 }} />

                    {/* Timeline Dinâmico de Status */}
                    <Box sx={{ position: 'relative', pl: 1, my: 2 }}>
                        {STEPS_CONFIG.map((step, index) => {
                            const isDone = !!historicoMap[step.key];
                            const subtitleText = isDone ? this.formatDate(historicoMap[step.key]) : 'Pendente';

                            return (
                                <Box
                                    key={step.key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        position: 'relative',
                                        mb: index !== STEPS_CONFIG.length - 1 ? 3 : 0,
                                    }}
                                >
                                    {index !== STEPS_CONFIG.length - 1 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 40,
                                                left: 20,
                                                width: 2,
                                                height: 'calc(100% - 16px)',
                                                bgcolor: '#E0E0E0',
                                                zIndex: 1,
                                            }}
                                        />
                                    )}

                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            bgcolor: isDone ? '#E8F5E9' : '#F5F5F5',
                                            color: isDone ? '#4CAF50' : '#9E9E9E',
                                            mr: 2,
                                            zIndex: 2,
                                        }}
                                    >
                                        {step.icon}
                                    </Avatar>

                                    <Box sx={{ pt: 0.5 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: isDone ? '#263238' : '#78909C',
                                            }}
                                        >
                                            {step.label}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#78909C', display: 'block' }}>
                                            {subtitleText}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Divider sx={{ borderStyle: 'dashed', my: 2 }} />

                    {/* Seção Pedido e Lista de Itens */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1F2D3D', mb: 2 }}>
                        Pedido N° {data.id}
                    </Typography>

                    {(data.itens || []).map((item) => (
                        <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        border: '1px solid #CFD8DC',
                                        borderRadius: '4px',
                                        px: 0.8,
                                        py: 0.2,
                                        height: 'fit-content',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#37474F' }}>
                                        {item.quantidade}x
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: '500', color: '#263238' }}>
                                        {item.produto_nome}
                                    </Typography>
                                    {item.obs && (
                                        <Typography variant="caption" sx={{ color: '#78909C', display: 'block' }}>
                                            {item.obs}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: '500', color: '#263238' }}>
                                {this.formatCurrency(item.valor_total || item.valor_venda * item.quantidade)}
                            </Typography>
                        </Box>
                    ))}

                    <Divider sx={{ borderStyle: 'dotted', my: 2 }} />

                    {/* Subtotal e Total */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#78909C' }}>
                            Subtotal
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#78909C' }}>
                            {this.formatCurrency(valorExibicao)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1F2D3D' }}>
                            Total
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1F2D3D' }}>
                            {this.formatCurrency(valorExibicao)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default WithRouter(DetalhesPedido);